import {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '../auth'
import {hidesPayments} from '../install-app/core/androidApp'
import {
  INVOICE_ALERTS_INTRO_EVENT,
  invoiceAlertsSettingsPath,
  PERIODIC_REPORTS_INTRO_EVENT,
  periodicReportsIntroPath,
  shouldAnnounceBusinessServices,
  TAX_WORKSPACE_ALERTS_INTRO_EVENT,
  taxWorkspaceAlertsSettingsPath,
} from './businessServicesIntro'
import '../apps/invoice-management/invoice-print/intro/printPageIntro.scss'

type Feature = {icon: string; title: string; text: string}

type IntroContent = {
  id: string
  event: string
  title: string
  lead: string
  features: Feature[]
  costTitle: string
  cost: string
  action: string
  actionIcon: string
  path: (companyId: number) => string
}

const FEE_NOTE =
  'معتمد آسا ممکن است برای این سرویس هزینه‌ای هم تعیین کند؛ اگر چنین باشد، پیش از فعال‌سازی مبلغ آن نشان داده می‌شود و با تأیید شما از کیف پول کم می‌شود.'

const INVOICE_ALERTS: IntroContent = {
  id: 'invoice-alerts-intro',
  event: INVOICE_ALERTS_INTRO_EVENT,
  title: 'اعلان‌های صورتحساب',
  lead: 'رویدادهای مهم صورتحساب‌های کسب‌وکار را همان لحظه بدانید: در پنل، تلگرام و مرورگر رایگان، و اگر بخواهید با پیامک.',
  features: [
    {
      icon: 'la-user-check',
      title: 'واکنش خریدار در کارپوشه',
      text: 'تأیید یا رد صورتحساب توسط خریدار، و تأیید یا رد سیستمی وقتی مهلت واکنش تمام می‌شود.',
    },
    {
      icon: 'la-balance-scale',
      title: 'عبور از حد مجاز ماده ۶',
      text: 'همین که صورتحسابی از حد مجاز ماده ۶ عبور کند، باخبر می‌شوید.',
    },
    {
      icon: 'la-exclamation-triangle',
      title: 'خطای ارسال صورتحساب',
      text: 'وقتی سازمان صورتحسابی را رد کند یا ارسال آن ناموفق بماند، با متن خطا.',
    },
    {
      icon: 'la-layer-group',
      title: 'پایان ارسال گروهی',
      text: 'پس از هر ارسال گروهی از تعدادی که خودتان تعیین می‌کنید به بالا، یک اعلان با شمار ثبت‌شده، رد سازمان، در انتظار نتیجه و ارسال‌نشده.',
    },
  ],
  costTitle: 'قانون‌ها و هزینه',
  cost: `برای همهٔ خریداران یا خریداران خاص، و موضوع و الگوی دلخواه قانون بسازید. «نوع صورتحساب ۲» خریدار ندارد، پس واکنش خریدار برای آن پیش نمی‌آید. هزینهٔ پیامک از کیف پول کسب‌وکار کم می‌شود. ${FEE_NOTE}`,
  action: 'تنظیم اعلان‌های صورتحساب',
  actionIcon: 'la-bell',
  path: invoiceAlertsSettingsPath,
}

const PERIODIC_REPORTS: IntroContent = {
  id: 'periodic-reports-intro',
  event: PERIODIC_REPORTS_INTRO_EVENT,
  title: 'گزارش دوره‌ای',
  lead: 'گزارش درج و ارسال صورتحساب‌ها و وضعیت کلی فصل، هر ۶ ساعت تا هر ۳۰ روز، با پیامک برای شما و همکارانتان.',
  features: [
    {
      icon: 'la-clock',
      title: 'زمان‌بندی دلخواه',
      text: 'چند گزارش با بازه، ساعت ارسال و گیرندگان جدا؛ هر وقت خواستید «اجرای همین حالا» را بزنید.',
    },
    {
      icon: 'la-filter',
      title: 'همان فیلترهای فهرست صورتحساب‌ها',
      text: 'از فهرست صورتحساب‌ها با «گزارش دوره‌ای با این فیلترها» گزارشی با همان فیلترها بسازید.',
    },
    {
      icon: 'la-sms',
      title: 'پیامک به اندازهٔ نیاز',
      text: 'بخش‌های پیامک را خودتان انتخاب کنید و پیش از ذخیره، متن و شمار بخش‌ها و هزینهٔ آن را ببینید.',
    },
    {
      icon: 'la-chart-bar',
      title: 'گزارش کامل در پنل',
      text: 'هر اجرا صفحه‌ای با نمودار، شمارها، خطاها و خریداران اصلی دارد؛ قابل چاپ و خروجی اکسل.',
    },
  ],
  costTitle: 'هزینه',
  cost: `گزارش کامل در پنل رایگان است و هزینهٔ پیامک از کیف پول کسب‌وکار کم می‌شود. ${FEE_NOTE}`,
  action: 'ساخت گزارش دوره‌ای',
  actionIcon: 'la-chart-bar',
  path: periodicReportsIntroPath,
}

const TAX_WORKSPACE_ALERTS: IntroContent = {
  id: 'tax-workspace-alerts-intro',
  event: TAX_WORKSPACE_ALERTS_INTRO_EVENT,
  title: 'اعلان‌های کارپوشه و تأیید خودکار صورتحساب‌های خرید',
  lead: 'آنچه در کارپوشهٔ سامانهٔ مؤدیان منتظر واکنش شماست، بی‌آنکه هر روز به کارپوشه سر بزنید: در پنل و تلگرام رایگان، و اگر بخواهید با پیامک.',
  features: [
    {
      icon: 'la-inbox',
      title: 'منتظر واکنش شما',
      text: 'صورتحساب‌های خرید، صورتحساب‌های مجازی و قراردادهای شخص ثالث تازه، هر چند ساعت که خودتان تعیین می‌کنید.',
    },
    {
      icon: 'la-robot',
      title: 'پایان مهلت واکنش و واکنش خریدار',
      text: 'تأیید یا رد سیستمی پس از پایان مهلت، و واکنش خریدار به فروش‌هایی که با نرم‌افزار دیگری ثبت شده‌اند.',
    },
    {
      icon: 'la-key',
      title: 'وضعیت و دسترسی کارپوشه',
      text: 'فعال نبودن کارپوشهٔ مؤدی و پایان یا نزدیک شدن پایان تفویض دسترسی، پیش از آنکه ارسال یا واکنش‌ها متوقف شود.',
    },
    {
      icon: 'la-check-double',
      title: 'تأیید خودکار صورتحساب‌های خرید',
      text: 'با قانونی که خودتان می‌سازید: فقط فروشندگانی که انتخاب می‌کنید، با سقف مبلغ و الگوی دلخواه؛ به‌طور پیش‌فرض خاموش است.',
    },
  ],
  costTitle: 'پیش از روشن کردن',
  cost: `بررسی با تفویض دسترسی کارپوشهٔ همین کسب‌وکار انجام می‌شود. تأیید خودکار اعتبار مالیاتی می‌سازد و برگشت ساده‌ای ندارد؛ پیش از روشن کردن آن، پیامدش را می‌خوانید و تأیید می‌کنید. «بررسی وضعیت کارپوشه پیش از ارسال» هم در همین صفحه فعال می‌شود. هزینهٔ پیامک از کیف پول کسب‌وکار کم می‌شود. ${FEE_NOTE}`,
  action: 'تنظیم اعلان‌های کارپوشه',
  actionIcon: 'la-inbox',
  path: taxWorkspaceAlertsSettingsPath,
}

/** One introduction, opened only from its item in «امکانات تازه»; the button opens the selected business's tab. */
const ServiceIntroGate: FC<{content: IntroContent}> = ({content}) => {
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const [show, setShow] = useState(false)

  useEffect(() => {
    const open = () => setShow(true)
    window.addEventListener(content.event, open)
    return () => window.removeEventListener(content.event, open)
  }, [content.event])

  if (!show || !shouldAnnounceBusinessServices(currentUser)) return null

  const close = () => setShow(false)
  const go = () => {
    setShow(false)
    navigate(content.path(Number(currentUser?.cpi || 0)))
  }

  return (
    <Modal
      show
      onHide={close}
      centered
      size='lg'
      fullscreen='sm-down'
      scrollable
      dialogClassName='ppi-modal'
      aria-labelledby={`${content.id}-title`}
    >
      <Modal.Body className='ppi-modal__body'>
        <button type='button' className='ppi-modal__close' onClick={close} aria-label='بستن'>
          <i className='las la-times' aria-hidden='true' />
        </button>

        <div className='ppi-content'>
          <span className='ppi-eyebrow'>
            <i className='las la-star' aria-hidden='true' />
            تازه
          </span>
          <h2 id={`${content.id}-title`} className='ppi-title'>
            {content.title}
          </h2>
          <p className='ppi-lead'>{content.lead}</p>

          <ul className='ppi-features' aria-label={`امکانات ${content.title}`}>
            {content.features.map((feature) => (
              <li key={feature.title} className='ppi-feature'>
                <span className='ppi-feature__icon' aria-hidden='true'>
                  <i className={`las ${feature.icon}`} />
                </span>
                <span>
                  <strong>{feature.title}</strong>
                  <span className='ppi-feature__text'>{feature.text}</span>
                </span>
              </li>
            ))}
          </ul>

          {/* No fee is spoken of in the Bazaar and Myket builds of the Android app. */}
          <section className='ppi-section' aria-labelledby={`${content.id}-cost`} hidden={hidesPayments()}>
            <h3 id={`${content.id}-cost`} className='ppi-section__title'>
              {content.costTitle}
            </h3>
            <p className='ppi-note'>{content.cost}</p>
          </section>
        </div>
      </Modal.Body>
      <Modal.Footer className='ppi-modal__footer'>
        <button type='button' className='btn btn-light' onClick={close}>
          بستن
        </button>
        <button type='button' className='btn btn-primary' onClick={go}>
          <i className={`las ${content.actionIcon}`} aria-hidden='true' />
          {content.action}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export const InvoiceAlertsIntroGate: FC = () => <ServiceIntroGate content={INVOICE_ALERTS} />

export const PeriodicReportsIntroGate: FC = () => <ServiceIntroGate content={PERIODIC_REPORTS} />

export const TaxWorkspaceAlertsIntroGate: FC = () => (
  <ServiceIntroGate content={TAX_WORKSPACE_ALERTS} />
)
